import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import { toast } from "react-toastify";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import { Footer } from "../../Footer";
import { GetBroadcastRecipients, SendBroadcast as SendBroadcastRequest } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import {
  ADMIN_NO_PLAYERS_MESSAGE,
  BROADCASTS_PATH,
  MESSAGE_MAX_LENGTH,
  NO_REPLY_ALERT,
  SUBJECT_MAX_LENGTH,
  allOptedOutMessage,
  chipsView,
  hasErrors,
  isServerFailure,
  optedOutLabel,
  summaryLabel,
  toggleSelected,
  unverifiedLabel,
  validateBroadcast,
} from "../../utils/helper/broadcasts";
import { BroadcastAlert } from "./BroadcastAlert.jsx";
import { RecipientPicker } from "./RecipientPicker.jsx";

/** An inline error under a field. */
const FieldError = ({ id, message }) =>
  message ? (
    <div className="broadcast-error" id={id} role="alert">
      <icons.ExclamationCircle aria-hidden="true" />
      {message}
    </div>
  ) : null;

/**
 * MG-014 / AD-013 (#167, #168): Send Broadcast. The Manager or one of their Admins picks players,
 * writes a subject and a plain-text message, and sends. The server only saves the broadcast and
 * returns; the emails go out in the background, and Broadcasting Center shows the new row at once.
 *
 * Send is always enabled (AC3): a click with a field missing sends nothing and shows an error under
 * each missing field, and each error clears once its field is filled in.
 */
export const SendBroadcast = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(false);
  const [compose, setCompose] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sending, setSending] = useState(false);
  const [noReply, setNoReply] = useState(false);

  function sidebarfun() {
    setData(!data);
  }

  useEffect(() => {
    let current = true;
    GetBroadcastRecipients().then((res) => {
      if (!current) return;
      if (res?.data?.status) {
        setCompose(res.data);
      } else {
        setLoadFailed(true);
        // A 5xx or no reply has already been toasted by apiClient.
        if (res && res.status < 500) toast.error(readApiMessage(res));
      }
    });
    return () => {
      current = false;
    };
  }, []);

  const players = useMemo(() => compose?.players || [], [compose]);
  const loaded = compose !== null;
  const noPlayers = loaded && players.length === 0;
  // Approved players who turned the emails off, or have not verified their email, are not in
  // `players`; the page says how many of each.
  const optedOut = optedOutLabel(compose?.optedOutCount);
  const unverified = unverifiedLabel(compose?.unverifiedCount);
  const formDisabled = !loaded || noPlayers;
  const errors = attempted ? validateBroadcast({ selectedCount: selected.size, subject, message }) : {};
  const chips = chipsView(players, selected);

  const backToList = () => navigate(BROADCASTS_PATH);

  const handleSend = async (event) => {
    event.preventDefault();
    if (sending || formDisabled) return;

    setAttempted(true);
    setNoReply(false);
    if (hasErrors(validateBroadcast({ selectedCount: selected.size, subject, message }))) return;

    setSending(true);
    const res = await SendBroadcastRequest({
      playerIds: [...selected],
      subject: subject.trim(),
      message,
    });
    setSending(false);

    if (res?.data?.status) {
      navigate(BROADCASTS_PATH, {
        state: { justSentId: res.data.broadcastId, sentCount: res.data.recipientCount },
      });
    } else if (isServerFailure(res)) {
      // No reply, or a 5xx - a gateway timeout, or a fault after usp_InsertBroadcast committed: the
      // broadcast may have been saved, so sending again could send it twice. apiClient has already
      // toasted whatever needed a toast.
      setNoReply(true);
    } else {
      toast.error(readApiMessage(res));
    }
  };

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div className={data ? "dashboard-content full-contnet" : "dashboard-content"}>
          <Link to={BROADCASTS_PATH} className="page-back">
            <icons.ArrowLeft aria-hidden="true" />
            Broadcasting Center
          </Link>
          <h1 className="heading-dash page-heading">Send Broadcast</h1>

          <div className="group_data_table broadcast-card">
            {noReply && (
              <BroadcastAlert
                variant="warning"
                title={NO_REPLY_ALERT.title}
                dismissible
                onClose={() => setNoReply(false)}
              >
                {NO_REPLY_ALERT.text}
              </BroadcastAlert>
            )}

            {noPlayers && (
              <BroadcastAlert variant="info">
                {compose.optedOutCount > 0 || compose.unverifiedCount > 0 ? (
                  allOptedOutMessage(compose.isManager, compose.unverifiedCount)
                ) : compose.isManager ? (
                  <>
                    You don't have any approved players yet. To send a broadcast, approve player
                    subscriptions on the <Link to="/SuperAdmin/UserSuper">Players</Link> page.
                  </>
                ) : (
                  ADMIN_NO_PLAYERS_MESSAGE
                )}
              </BroadcastAlert>
            )}

            {loadFailed && (
              <BroadcastAlert variant="danger">
                The players could not be loaded. Reload the page to try again.
              </BroadcastAlert>
            )}

            <Form noValidate onSubmit={handleSend} className="broadcast-form">
              <div className="broadcast-field">
                <span className="broadcast-label" id="broadcast-recipients-label">
                  Recipients<span className="broadcast-required">*</span>
                </span>
                <RecipientPicker
                  players={players}
                  selected={selected}
                  onChange={setSelected}
                  disabled={formDisabled}
                  noPlayers={noPlayers}
                  invalid={Boolean(errors.players)}
                />
                <FieldError id="broadcast-recipients-error" message={errors.players} />
                {!errors.players && loaded && !noPlayers && (
                  <div className="broadcast-hint">
                    {compose.isManager
                      ? "Only players who subscribed to you and whom you approved are listed."
                      : "Only players who subscribed to your Manager and whom they approved are listed."}
                  </div>
                )}
                {!errors.players && loaded && !noPlayers && optedOut && (
                  <div className="broadcast-hint">{optedOut}</div>
                )}
                {!errors.players && loaded && !noPlayers && unverified && (
                  <div className="broadcast-hint">{unverified}</div>
                )}
                {chips.shown.length > 0 && (
                  <div className="broadcast-chips">
                    {chips.shown.map((player) => (
                      <span className="broadcast-chip" key={player.playerId}>
                        {player.playerName}
                        <button
                          type="button"
                          aria-label={`Remove ${player.playerName}`}
                          onClick={() => setSelected(toggleSelected(selected, player.playerId))}
                        >
                          <icons.X aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                    {chips.more > 0 && <span className="broadcast-chip more">+{chips.more} more</span>}
                  </div>
                )}
              </div>

              <Form.Group className="broadcast-field" controlId="broadcast-subject">
                <Form.Label className="broadcast-label">
                  Subject<span className="broadcast-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="What is this update about?"
                  value={subject}
                  maxLength={SUBJECT_MAX_LENGTH}
                  disabled={formDisabled}
                  isInvalid={Boolean(errors.subject)}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <FieldError id="broadcast-subject-error" message={errors.subject} />
              </Form.Group>

              <Form.Group className="broadcast-field" controlId="broadcast-message">
                <Form.Label className="broadcast-label">
                  Message<span className="broadcast-required">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  className="broadcast-textarea"
                  placeholder="Write your message to players"
                  value={message}
                  maxLength={MESSAGE_MAX_LENGTH}
                  disabled={formDisabled}
                  isInvalid={Boolean(errors.message)}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <div className="broadcast-message-meta">
                  {errors.message ? (
                    <FieldError id="broadcast-message-error" message={errors.message} />
                  ) : (
                    <span className="broadcast-hint">
                      Plain text only. Attachments, images and formatting aren’t supported.
                    </span>
                  )}
                  <span className="broadcast-counter">
                    {message.length.toLocaleString("en-US")} / {MESSAGE_MAX_LENGTH.toLocaleString("en-US")}
                  </span>
                </div>
              </Form.Group>

              <div className="broadcast-sendbar">
                <div className="broadcast-summary">
                  <span className={`broadcast-count${selected.size ? "" : " zero"}`}>
                    <icons.PeopleFill aria-hidden="true" />
                    {summaryLabel(selected.size)}
                  </span>
                  {compose?.senderDisplayName && (
                    <span className="broadcast-from">
                      Sender: <b>{compose.senderDisplayName}</b>
                    </span>
                  )}
                </div>
                <div className="broadcast-actions">
                  <button type="button" className="broadcast-btn broadcast-btn-ghost" onClick={backToList}>
                    Cancel
                  </button>
                  <button type="submit" className="broadcast-btn broadcast-btn-primary" disabled={sending || formDisabled}>
                    {sending ? (
                      <>
                        <icons.ArrowRepeat aria-hidden="true" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <icons.Send aria-hidden="true" />
                        Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
    </>
  );
};
